import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import { Alert, Stack, styled, Tab, Tabs } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from 'thermia-react-container';
import DevicesIcon from '../../../components/assets/icons/devicesIcon';
import TabCustomLabel from '../../../components/tabCustomLabel';
import { DeviceStatus } from '../../../types/device';
import { Registration } from '../../../types/registrationTypes';
import { getRegistrationStatus } from '../../../validation/registration';
import { NEW_REGISTRATION_ID } from '../registration';

interface Props {
  registration?: Registration | null;
  hasUnsavedData: boolean;
  preventSwitchTabDueToUnsavedData: (value: boolean) => void;
  disabled: boolean;
  registrationIsCompleted: boolean;
}

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const RegistrationTabs: FC<Props> = ({ hasUnsavedData, preventSwitchTabDueToUnsavedData, registration, disabled, registrationIsCompleted }) => {
  const [preventedToGoToTab, setPreventedToGoToTab] = useState<string | null>(null);
  let { id = NEW_REGISTRATION_ID, tabPage = 'customer' } = useParams<{ id?: string; tabPage: string }>();

  const { t } = useTranslation('registrations');

  useEffect(() => {
    if (preventedToGoToTab && !hasUnsavedData) {
      setPreventedToGoToTab(null);
    }
  }, [hasUnsavedData, preventedToGoToTab]);

  const { devicesIsComplete, heatingSetupIsComplete, locationIsComplete, customerIsComplete } = useMemo(
    () => getRegistrationStatus(registration ?? { id: 'no-active', partnerId: 'no-active' }),
    [registration]
  );

  const navigate = useNavigate();
  const [value, setValue] = useState(tabPage);

  const handleChange = (_: any, newValue: string) => {
    if (hasUnsavedData) {
      return setPreventedToGoToTab(newValue);
    }

    handleSwitchPage(newValue);
  };

  const hasInValidDevice = (): boolean => {
    const devices = registration?.devices?.items || [];
    if (devices.length === 0) {
      return false;
    }
    if (registrationIsCompleted) {
      return devices.some((device) => device.status !== DeviceStatus.installed);
    }
    return devices.some((device) => device.status === DeviceStatus.installed);
  };

  const handleSwitchPage = (goToTab: string) => {
    let url = '';
    if (id === NEW_REGISTRATION_ID) {
      url = `/new-registration/${goToTab}`;
    } else {
      if (registrationIsCompleted) {
        url = `/completed-registrations/${id}/${goToTab}`;
      } else {
        url = `/registrations/${id}/${goToTab}`;
      }
    }
    navigate(url, { state: { registrationIsCompleted: disabled } });
    setValue(goToTab);
    setPreventedToGoToTab(null);
    preventSwitchTabDueToUnsavedData(false);
  };

  const handleAccept = () => {
    if (preventedToGoToTab) {
      handleSwitchPage(preventedToGoToTab);
    }
  };

  return (
    <>
      <StyledTabs variant="fullWidth" style={{ marginBottom: '20px' }} value={value} onChange={handleChange} aria-label="registration tabs">
        <Tab
          value={'customer'}
          icon={<AccountCircleOutlinedIcon />}
          iconPosition={'start'}
          label={
            <TabCustomLabel label={t('registrations.general.customer')} isCompleted={customerIsComplete} isInvalid={false} hideCircle={registrationIsCompleted} />
          }
        />
        <Tab
          value={'location'}
          icon={<LocationOnOutlinedIcon />}
          iconPosition={'start'}
          label={
            <TabCustomLabel label={t('registrations.general.location')} isCompleted={locationIsComplete} isInvalid={false} hideCircle={registrationIsCompleted} />
          }
        />
        <Tab
          value={'devices'}
          icon={<DevicesIcon />}
          iconPosition={'start'}
          label={
            <TabCustomLabel
              label={t('registrations.general.device', { context: 'plural' })}
              isInvalid={hasInValidDevice()}
              isCompleted={devicesIsComplete}
              hideCircle={registrationIsCompleted}
            />
          }
        />
        <Tab
          value={'system'}
          icon={<SettingsInputComponentOutlinedIcon />}
          iconPosition={'start'}
          label={
            <TabCustomLabel label={t('registrations.general.system')} isCompleted={heatingSetupIsComplete} isInvalid={false} hideCircle={registrationIsCompleted} />
          }
        />
      </StyledTabs>
      {preventedToGoToTab !== null && (
        <Stack sx={{ width: '100%' }} spacing={2}>
          <Alert
            severity="warning"
            action={<Button title={t('registrations.general.continueAndDischargeChanges')} variant="outlined" onClick={handleAccept} />}>
            {t('registrations.general.warningYouHaveUnsavedData')}
          </Alert>
        </Stack>
      )}
    </>
  );
};

export default RegistrationTabs;
